import React, { useContext, useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Tab,
  Nav,
  Button,
  Accordion,
} from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  Bed,
  User,
  TentTree,
  Calendar,
  Car,
  Ticket,
  TicketCheck,
  Mountain,
} from "lucide-react";

import "../css/AccountDetails.css";
import AccountInfo from "./AccountInfo";
import RoomBookingList from "./RoomBookingList";
import TourBookingList from "./TourBookingList";
import SightseeingBookingList from "./SightseeingBookingList.jsx";
import CabRentalBookingList from "./CabBookingList";

const AccountDetails = () => {
  const { currentUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(location.state?.tab || "details");
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  const updateMedia = () => setIsDesktop(window.innerWidth >= 768);
  useEffect(() => {
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  }, []);

  const handleLogout = () => {
    const confirmLogout = window.confirm("Are you sure you want to logout?");
    if (!confirmLogout) return;
    logout();
    localStorage.removeItem("userdetails");
    navigate("/account");
  };

  return (
    <Container fluid className="mt-3 px-3 px-md-5" style={{ minHeight: "90vh" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 sticky-top py-2 px-2 " style={{ zIndex: 1000 }}>
        <h5 className="mb-0">Hello, {currentUser?.name || "User"}!</h5>
        <Button
          variant="outline-danger"
          size="sm"
          style={{ borderRadius: "20px" }}
          onClick={handleLogout}
        >
          <LogOut size={14} /> Logout
        </Button>
      </div>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Row>
          {/* Desktop Sidebar */}
          {isDesktop ? (
            <Col md={3}>
              <div className="sticky-sidebar">
                <Nav
                  variant="pills"
                  className="flex-column shadow-sm p-3 bg-white rounded mb-3"
                >
                  <Nav.Item>
                    <Nav.Link eventKey="details">Profile Details</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="room">Room Bookings</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="package">Tour Packages</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="sightseeing">Sightseeing Bookings</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="cab">Cab Bookings</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link onClick={handleLogout}>Logout</Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>
            </Col>
          ) : (
            // ✅ Mobile Tabs (Only 2)
            <Col xs={12} className="mb-3">
               <div className="sticky-tabbar">
                      <Nav
                        variant="pills"
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="d-flex justify-content-around text-center  flex-nowrap overflow-auto custom-tabs"
                      >
                        
                        <Nav.Item className="flex-fill text-nowrap">
                          <Nav.Link eventKey="details" className="tab-link ">
                            <User size={20} className="mb-1" />
                            <br />
                            <small>My Profile</small>
                          </Nav.Link>
                        </Nav.Item>
              
                        <Nav.Item className="flex-fill text-nowrap">
                          <Nav.Link eventKey="bookings" className="tab-link">
                            <TicketCheck size={20} className="mb-1" />
                            <br />
                            <small>My Bookings</small>
                          </Nav.Link>
                        </Nav.Item>
              
                       
                      </Nav>
                    </div>
             
            </Col>
          )}

          {/* Main Content */}
          <Col md={isDesktop ? 9 : 12}>
            <Tab.Content>
              {/* Profile Tab */}
              <Tab.Pane eventKey="details">
                <div className="shadow-sm p-4 bg-white border-cus">
                  <AccountInfo />
                </div>
              </Tab.Pane>

              {/* Desktop Tabs */}
              {isDesktop && (
                <>
                  <Tab.Pane eventKey="room">
                    <div className="shadow-sm p-4 bg-white border-cus">
                      <RoomBookingList />
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="package">
                    <div className="shadow-sm p-4 bg-white border-cus">
                      <TourBookingList />
                    </div>
                  </Tab.Pane>
                   <Tab.Pane eventKey="sightseeing">
                    <div className="shadow-sm p-4 bg-white border-cus">
                      <SightseeingBookingList />
                    </div>
                  </Tab.Pane>
                 <Tab.Pane eventKey="cab">
                    <div className="shadow-sm p-4 bg-white border-cus">
                      <CabRentalBookingList />
                    </div>
                  </Tab.Pane>
                </>
              )}

              {/* ✅ Mobile Accordion inside My Bookings */}
              {!isDesktop && (
                <Tab.Pane eventKey="bookings">
                  <div className="shadow-sm p-3 bg-white border-cus">
                    <Accordion alwaysOpen>
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          <Bed size={16} className="me-2" /> Room Bookings
                        </Accordion.Header>
                        <Accordion.Body>
                          <RoomBookingList />
                        </Accordion.Body>
                      </Accordion.Item>

                      <Accordion.Item eventKey="1">
                        <Accordion.Header>
                          <TentTree size={16} className="me-2" /> Tour Packages
                        </Accordion.Header>
                        <Accordion.Body>
                          <TourBookingList />
                        </Accordion.Body>
                      </Accordion.Item>

                      <Accordion.Item eventKey="2">
                        <Accordion.Header>
                          <Mountain size={16} className="me-2" /> Sightseeing Bookings
                        </Accordion.Header>
                        <Accordion.Body>
                          <SightseeingBookingList />
                        </Accordion.Body>
                      </Accordion.Item>
 
                      <Accordion.Item eventKey="3">
                        <Accordion.Header>
                          <Car size={16} className="me-2" /> Cab Bookings
                        </Accordion.Header>
                        <Accordion.Body>
                          <CabRentalBookingList />
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </div>
                </Tab.Pane>
              )}
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </Container>
  );
};

export default AccountDetails;
